import {computed, Injectable, isDevMode, Signal, signal} from '@angular/core';

/**
 * Debug/observability sink for {@link DuplicateRequestInterceptor}.
 *
 * The interceptor collapses identical, concurrent mutating requests onto a single in-flight backend call
 * (see `docs/articles/frontend/http-request-deduplication.md`). Because that collapsing is invisible to the
 * caller — a deduped request looks like any other successful request — a developer chasing "my second POST
 * never hit the server" has nothing to grab onto. This service is that grab: a signal-backed live view of
 * how many distinct requests are in flight, how many callers are currently *waiting* on a shared response,
 * and how many duplicates have been collapsed in total this session. It carries no behaviour of its own — the
 * interceptor is the single writer; everything here is either a state mutation it calls or a read-only signal
 * a debugger (or a future dev overlay) can watch.
 */
@Injectable({
  providedIn: 'root'
})
export class RequestDedupeMonitor {
  /** Number of distinct mutating requests currently in flight (i.e. live dedupe keys). */
  private readonly _inFlightKeys = signal(0);

  /** Number of *duplicate* callers currently waiting on a shared in-flight response (excludes the originators). */
  private readonly _waiting = signal(0);

  /** Monotonic count of duplicates collapsed since app start — never reset; a pure debug odometer. */
  private readonly _totalDeduped = signal(0);

  /** Distinct mutating requests currently in flight (one per live dedupe key). */
  readonly inFlightKeys: Signal<number> = this._inFlightKeys.asReadonly();

  /** Duplicate callers currently sharing an in-flight response (the ones that never reached the backend). */
  readonly waiting: Signal<number> = this._waiting.asReadonly();

  /** Total duplicates collapsed since app start. A debug odometer — it only ever increases. */
  readonly totalDeduped: Signal<number> = this._totalDeduped.asReadonly();

  /** True while at least one duplicate is being shared — handy for a dev overlay to show/hide itself. */
  readonly hasActivity: Signal<boolean> = computed(() => this._inFlightKeys() > 0);

  /**
   * A new distinct request has become the in-flight originator for {@link key}.
   * @param key The dedupe key (`method + ' ' + urlWithParams + ' ' + serializedBody`).
   */
  registerInFlight(key: string): void {
    this._inFlightKeys.update(n => n + 1);
    this.log(`in-flight +1 (${this._inFlightKeys()} keys): ${key}`);
  }

  /**
   * A duplicate caller has attached to an already in-flight request, sharing its response instead of hitting
   * the backend. Increments both the live waiting count and the session odometer.
   * @param key The dedupe key the duplicate attached to.
   */
  registerDuplicate(key: string): void {
    this._waiting.update(n => n + 1);
    this._totalDeduped.update(n => n + 1);
    this.log(`deduped (${this._waiting()} waiting, ${this._totalDeduped()} total): ${key}`);
  }

  /**
   * An in-flight request has settled (completed, errored, or been cancelled) and its shared observable is
   * being torn down. Drops the in-flight key and releases the {@link waitingCount} duplicates that were
   * attached to it back to zero-for-this-key.
   * @param key The dedupe key that settled.
   * @param waitingCount How many duplicates had attached to this key (so the waiting signal can be decremented
   *   by exactly that many — never below zero).
   */
  settle(key: string, waitingCount: number): void {
    this._inFlightKeys.update(n => Math.max(0, n - 1));
    if (waitingCount > 0) {
      this._waiting.update(n => Math.max(0, n - waitingCount));
    }
    this.log(`settled (${this._inFlightKeys()} keys, ${this._waiting()} waiting): ${key}`);
  }

  /** Dev-only console breadcrumb; silent in production so the odometer never costs anything at runtime. */
  private log(message: string): void {
    if (isDevMode()) {
      // eslint-disable-next-line no-console
      console.debug(`[dedupe] ${message}`);
    }
  }
}
